import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import {
  enablePushNotifications,
  disablePushNotifications,
  isPushSubscribed,
  pushPermission,
  pushSupported,
} from "@/lib/push";
import { track } from "@/lib/analytics";

/**
 * NotifyMeButton — small persistent toggle so installed users can manage
 * their push subscription independently of the install flow. Auto-hides on
 * browsers that don't support web push.
 */
export default function NotifyMeButton({ variant = "compact" }) {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [perm, setPerm] = useState("unsupported");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    setSupported(true);
    setPerm(pushPermission());
    (async () => {
      setSubscribed(await isPushSubscribed());
    })();
  }, []);

  if (!supported || perm === "denied") return null;

  const handleClick = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        const ok = await disablePushNotifications();
        if (ok) {
          setSubscribed(false);
          toast("Notifications turned off");
        }
      } else {
        const sub = await enablePushNotifications();
        if (sub) {
          setSubscribed(true);
          setPerm(pushPermission());
          track("push_optin");
          toast.success("You're in — we'll ping you on big news.");
        } else if (pushPermission() === "denied") {
          setPerm("denied");
          toast.error("Notifications blocked. Enable them in your browser settings to subscribe.");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const isHero = variant === "hero";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid={subscribed ? "notify-me-on-btn" : "notify-me-off-btn"}
      title={subscribed ? "You're subscribed — click to unsubscribe" : "Get notified about Titans news"}
      className={
        isHero
          ? `inline-flex items-center gap-2 font-cond uppercase tracking-wider px-6 py-3 text-sm md:text-base transition-colors ${
              subscribed
                ? "bg-titans-elev text-titans-silver hover:text-white"
                : "bg-titans-blue hover:bg-titans-blue-hover text-white"
            }`
          : `inline-flex items-center gap-2 font-cond uppercase tracking-wider text-xs border px-3 py-1.5 transition-colors ${
              subscribed
                ? "border-[rgba(138,141,143,0.3)] text-titans-silver hover:text-white hover:border-white"
                : "border-titans-red text-titans-red hover:bg-titans-red hover:text-white"
            }`
      }
    >
      {subscribed ? <BellOff size={isHero ? 16 : 12} /> : <Bell size={isHero ? 16 : 12} />}
      <span>
        {busy
          ? subscribed
            ? "Turning off…"
            : "Subscribing…"
          : subscribed
          ? "Notifying"
          : "Notify Me"}
      </span>
    </button>
  );
}
